import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";

/**
 * ConfirmDialog — Modal de confirmación reutilizable (reemplaza `window.confirm`).
 *
 * Props:
 *  - open: bool
 *  - onOpenChange: (open: bool) => void  — handler del Dialog raíz
 *  - title: string
 *  - description: ReactNode | string — cuerpo del mensaje
 *  - confirmLabel: string (default "Confirmar")
 *  - cancelLabel: string (default "Cancelar")
 *  - onConfirm: async () => void  — handler de confirmación
 *  - loading: bool — desactiva botones y muestra "…" en confirm
 *  - destructive: bool — usa color rojo (variant="destructive")
 *  - testIdPrefix: string — para data-testid (default "confirm")
 *  - icon: ReactNode — opcional, ícono junto al título
 */
export const ConfirmDialog = ({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = "Confirmar",
  cancelLabel = "Cancelar",
  onConfirm,
  loading = false,
  destructive = false,
  testIdPrefix = "confirm",
  icon = null,
}) => {
  const handleConfirm = async () => {
    if (loading) return;
    try {
      await onConfirm?.();
    } catch {
      // El caller se encarga del toast/error; aquí solo no propagamos.
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md"
        data-testid={`${testIdPrefix}-dialog`}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {icon}
            <span>{title}</span>
          </DialogTitle>
          {description && (
            <DialogDescription className="pt-2 text-sm text-gray-600 leading-relaxed">
              {description}
            </DialogDescription>
          )}
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={loading}
            data-testid={`${testIdPrefix}-cancel-btn`}
          >
            {cancelLabel}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={loading}
            className={
              destructive
                ? "bg-destructive hover:bg-destructive/90 text-white"
                : "bg-brand-burgundy hover:bg-brand-burgundy-light text-white"
            }
            data-testid={`${testIdPrefix}-accept-btn`}
          >
            {loading ? "Procesando…" : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmDialog;
